/**
 * useVideoConverter.js — Browser-side MTS/MKV/AVI → MP4 conversion
 * ==================================================================
 *
 * Uses FFmpeg.wasm to convert unsupported video formats to MP4
 * directly in the browser (no server round-trip needed).
 *
 * Supported conversions:
 *   .mts, .m2ts  → .mp4   (AVCHD camcorder format)
 *   .mkv         → .mp4   (Matroska)
 *   .avi         → .mp4   (older cameras)
 *   .mpg, .mpeg  → .mp4   (legacy)
 *
 * Usage:
 *   const { convertToMp4, converting, progress, error } = useVideoConverter();
 *
 *   const handleFile = async (file) => {
 *     if (needsConversion(file)) {
 *       const mp4File = await convertToMp4(file);
 *       // use mp4File...
 *     }
 *   };
 *
 * Dependencies:
 *   npm install @ffmpeg/ffmpeg @ffmpeg/util
 *
 * @author Arena Stats / Paulo Brant
 * @date   2026-03-23
 */

import { useState, useRef, useCallback } from 'react';

/**
 * Hook for browser-side video conversion to MP4.
 */
export default function useVideoConverter() {
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);       // 0-100
  const [error, setError] = useState(null);
  const [statusText, setStatusText] = useState('');
  const ffmpegRef = useRef(null);
  const loadedRef = useRef(false);

  /**
   * Lazy-load FFmpeg.wasm (only when first conversion is requested).
   * ~31MB download on first use, cached by browser afterwards.
   */
  const loadFFmpeg = useCallback(async () => {
    if (loadedRef.current && ffmpegRef.current) return ffmpegRef.current;

    setStatusText('Carregando conversor de vídeo...');

    try {
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { toBlobURL } = await import('@ffmpeg/util');

      const ffmpeg = new FFmpeg();

      // Progress callback
      ffmpeg.on('progress', ({ progress: p }) => {
        setProgress(Math.round(p * 100));
      });

      ffmpeg.on('log', ({ message }) => {
        // Only show meaningful log messages
        if (message.includes('frame=') || message.includes('time=')) {
          const timeMatch = message.match(/time=(\d+:\d+:\d+\.\d+)/);
          if (timeMatch) {
            setStatusText(`Convertendo... ${timeMatch[1]}`);
          }
        }
      });

      // Load FFmpeg with ALL files as blob URLs (bypasses CRA webpack entirely).
      //
      // Why blob URLs for everything:
      //   @ffmpeg/ffmpeg bundles a worker (814.ffmpeg.js) that CRA webpack rewrites.
      //   Inside the webpack-bundled worker, import(coreURL) goes through
      //   webpack's __webpack_require__ — which can't resolve external/blob URLs.
      //   By providing workerURL as a blob, the Worker is created from raw JS
      //   (not webpack-processed), so its import() is the browser's native one.
      //
      // Files self-hosted in /public/ffmpeg/ (same-origin, no CORS, no CDN).
      const base = window.location.origin + '/ffmpeg';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${base}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, 'application/wasm'),
        workerURL: await toBlobURL(`${base}/814.ffmpeg.js`, 'text/javascript'),
      });

      ffmpegRef.current = ffmpeg;
      loadedRef.current = true;
      setStatusText('Conversor pronto');
      return ffmpeg;
    } catch (err) {
      console.error('[VideoConverter] Failed to load FFmpeg:', err);
      throw new Error(
        'Não foi possível carregar o conversor de vídeo. ' +
        'Tente converter o arquivo para MP4 antes de enviar.'
      );
    }
  }, []);

  /**
   * Convert a video file to MP4 format.
   *
   * @param {File} file — Input video file (.mts, .mkv, .avi, etc.)
   * @param {Object} [options]
   * @param {string} [options.codec='copy'] — Video codec ('copy' = fast remux, 'libx264' = re-encode)
   * @param {string} [options.crf='23']     — Quality (lower = better, 18-28 range)
   * @returns {Promise<File>} — Converted MP4 file
   */
  const convertToMp4 = useCallback(async (file, options = {}) => {
    if (!file) throw new Error('No file provided');

    setConverting(true);
    setProgress(0);
    setError(null);
    setStatusText('Preparando conversão...');

    try {
      const ffmpeg = await loadFFmpeg();
      const { fetchFile } = await import('@ffmpeg/util');

      const inputName = `input_${Date.now()}${getExtension(file.name)}`;
      const outputName = `${getBaseName(file.name)}.mp4`;

      // Write input file to FFmpeg virtual filesystem
      setStatusText(`Carregando ${file.name} (${formatSize(file.size)})...`);
      const fileData = await fetchFile(file);
      await ffmpeg.writeFile(inputName, fileData);

      // Build FFmpeg command
      // Strategy: try fast copy first, fall back to re-encode
      const codec = options.codec || 'copy';
      const args = ['-i', inputName];

      if (codec === 'copy') {
        // Fast: just remux container (MTS→MP4) without re-encoding
        // Works for most AVCHD/MTS files
        args.push('-c', 'copy', '-movflags', '+faststart');
      } else {
        // Re-encode: slower but guaranteed compatibility
        args.push(
          '-c:v', 'libx264',
          '-preset', 'fast',
          '-crf', options.crf || '23',
          '-c:a', 'aac',
          '-movflags', '+faststart',
        );
      }
      args.push(outputName);

      setStatusText('Convertendo para MP4...');
      await ffmpeg.exec(args);

      // Read output file
      const outputData = await ffmpeg.readFile(outputName);

      // Check if output is valid (copy mode may fail silently for some codecs)
      if (outputData.length < 1000 && codec === 'copy') {
        console.warn('[VideoConverter] Copy mode produced tiny output, re-encoding...');
        // Retry with re-encode
        await ffmpeg.exec([
          '-i', inputName,
          '-c:v', 'libx264', '-preset', 'fast', '-crf', '23',
          '-c:a', 'aac', '-movflags', '+faststart',
          outputName,
        ]);
        const reEncodedData = await ffmpeg.readFile(outputName);
        const mp4Blob = new Blob([reEncodedData.buffer], { type: 'video/mp4' });
        const mp4File = new File([mp4Blob], outputName, { type: 'video/mp4' });

        // Cleanup
        await ffmpeg.deleteFile(inputName);
        await ffmpeg.deleteFile(outputName);

        setConverting(false);
        setProgress(100);
        setStatusText(`✓ Convertido: ${outputName} (${formatSize(mp4File.size)})`);
        return mp4File;
      }

      const mp4Blob = new Blob([outputData.buffer], { type: 'video/mp4' });
      const mp4File = new File([mp4Blob], outputName, { type: 'video/mp4' });

      // Cleanup virtual filesystem
      await ffmpeg.deleteFile(inputName);
      await ffmpeg.deleteFile(outputName);

      setConverting(false);
      setProgress(100);
      setStatusText(`✓ Convertido: ${outputName} (${formatSize(mp4File.size)})`);

      return mp4File;
    } catch (err) {
      console.error('[VideoConverter] Conversion failed:', err);
      const msg = 'Falha na conversão do vídeo. Tente converter para MP4 externamente.';
      setError(msg);
      setConverting(false);
      setStatusText('');
      throw new Error(msg);
    }
  }, [loadFFmpeg]);

  return {
    convertToMp4,
    converting,
    progress,
    error,
    statusText,
  };
}

// ═══════════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════════

function getExtension(filename) {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.substring(idx) : '';
}

function getBaseName(filename) {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.substring(0, idx) : filename;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
